import React from "react";
import { Link, Head } from "@inertiajs/react";
import { Button } from "@/components/ui/button";
import { CALENDLY_COST_REVIEW_URL } from "@/lib/urls";
import {
  BarChart2,
  Search,
  GitPullRequest,
  ShieldCheck,
  AlertTriangle,
  ChevronRight,
  Server,
  Activity,
  Calendar,
  CheckCircle,
} from "lucide-react";
import Layout from "../Layout";
import { CallToAction } from "@/components/marketing/CallToAction";
import {
  LatestArticles,
  type Article,
} from "@/components/marketing/LatestArticles";
import {
  PullRequestPreview,
  InlineCode,
} from "@/components/marketing/PullRequestPreview";
import { DiffPreview, DiffLine } from "@/components/marketing/DiffPreview";
import { SlackMessagePreview } from "@/components/marketing/SlackMessagePreview";
import { ProductRecommendationsMockup } from "@/components/marketing/ProductRecommendationsMockup";
import { ProductHowItWorks } from "@/components/marketing/ProductHowItWorks";
import { ProductThresholds } from "@/components/marketing/ProductThresholds";
import { ProductGuardrails } from "@/components/marketing/ProductGuardrails";
import { FAQ, type FAQItem } from "@/components/marketing/FAQ";

// ── Hero ─────────────────────────────────────────────────────────────────────

const BREADCRUMB_JSON_LD = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: "https://infralyst.io",
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "EC2 Rightsizing",
      item: "https://infralyst.io/products/ec2",
    },
  ],
});

const SOFTWARE_APP_JSON_LD = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Infralyst EC2 Rightsizing",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description:
    "Analyze real CloudWatch utilization across your EC2 fleet. Infralyst generates ready-to-review Terraform PRs to rightsize instances and reduce costs.",
  url: "https://infralyst.io/products/ec2",
  offers: {
    "@type": "AggregateOffer",
    lowPrice: "0",
    highPrice: "99",
    priceCurrency: "USD",
    offerCount: 2,
    offers: [
      {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
        description: "Free plan with 3 PRs per workspace",
      },
      {
        "@type": "Offer",
        price: "99",
        priceCurrency: "USD",
        description: "Pro plan with unlimited PRs per workspace, billed monthly",
      },
    ],
  },
  publisher: {
    "@type": "Organization",
    name: "Infralyst",
    url: "https://infralyst.io",
  },
});

const RECOMMENDATIONS = [
  {
    name: "api-prod-01",
    subtext: "us-east-1 · 72 days",
    current: "m5.2xlarge",
    recommended: "m5.xlarge",
    cpuAvg: "4.2%",
    cpuPct: "18%",
    memAvg: "11%",
    memPct: "24%",
    savings: "$47/mo",
    days: 72,
    region: "us-east-1",
  },
  {
    name: "worker-batch-02",
    subtext: "us-east-1 · 52 days",
    current: "c5.xlarge",
    recommended: "c5.large",
    cpuAvg: "8.1%",
    cpuPct: "22%",
    memAvg: "12%",
    memPct: "28%",
    savings: "$23/mo",
    days: 52,
    region: "us-east-1",
  },
  {
    name: "cache-redis-03",
    subtext: "us-east-1 · 45 days",
    current: "r5.large",
    recommended: "r5.medium",
    cpuAvg: "6.8%",
    cpuPct: "19%",
    memAvg: "9%",
    memPct: "21%",
    savings: "$18/mo",
    days: 45,
    region: "us-east-1",
  },
];

function Hero(): React.ReactElement {
  return (
    <>
      <Head>
        <script type="application/ld+json">{BREADCRUMB_JSON_LD}</script>
        <script type="application/ld+json">{SOFTWARE_APP_JSON_LD}</script>
      </Head>
      <section className="px-4 pt-28 pb-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-12 items-center">
            {/* Left: text */}
            <div>
              {/* Breadcrumb */}
              <nav
                aria-label="Breadcrumb"
                className="flex items-center gap-1 text-sm text-surface-foreground/50 mb-6"
              >
                <span>Products</span>
                <ChevronRight className="size-3.5" />
                <span className="flex items-center gap-1 text-foreground font-medium">
                  <Server className="size-3.5" />
                  EC2
                </span>
              </nav>

              <h1 className="mb-5 text-5xl font-bold leading-tight tracking-tight md:text-6xl">
                EC2 <span className="text-primary">Rightsizing</span>
              </h1>
              <p className="mb-8 text-xl leading-relaxed text-surface-foreground/80">
                Infralyst analyzes CloudWatch data across your EC2 fleet and
                surfaces savings opportunities. When you're ready, generate
                ready to merge Terraform PRs from Slack or the dashboard.
              </p>
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <Button asChild size="lg">
                  <Link href="/signup">Start free with 3 PRs</Link>
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <a href={CALENDLY_COST_REVIEW_URL} target="_blank" rel="noopener noreferrer">
                    Book a demo
                  </a>
                </Button>
              </div>
              <ul className="flex flex-col gap-2">
                {["Unlimited free recommendations", "Read-only IAM role", "Your team reviews and merges every change"].map((point) => (
                  <li key={point} className="flex items-center gap-2 text-sm text-surface-foreground/60">
                    <CheckCircle className="size-4 text-green-500 flex-none" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: UI mockup */}
            <div className="lg:pl-4">
              <ProductRecommendationsMockup
                title="EC2 Recommendations"
                icon={Server}
                recommendations={RECOMMENDATIONS}
                footerText="Analyzed 47 instances across 3 regions"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// ── The Problem ───────────────────────────────────────────────────────────────

function TheProblem(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="text-4xl font-bold mb-6">
          You're paying for
          <br />
          EC2 capacity <span className="text-primary">you don't use</span>
        </h2>
        <p className="text-lg leading-relaxed text-surface-foreground/80 mb-4">
          Teams pick an EC2 instance type for peak load and move on. Utilization
          drops, workloads change, but the bill doesn't. Most engineers know
          this, but actually fixing it means auditing instances across every
          region, picking the right target type, updating the Terraform, and
          convincing your team nothing will break. So it never happens.
        </p>
        <p className="text-lg leading-relaxed text-surface-foreground/80">
          AWS tools like Cost Explorer and Compute Optimizer will flag the
          waste. But they stop at a recommendation. Infralyst picks up where
          they leave off: it analyzes your CloudWatch data, selects a
          conservative target, and generates a PR your team can review and
          merge.
        </p>
      </div>
    </section>
  );
}

// ── How the Analysis Works ────────────────────────────────────────────────────

const ANALYSIS_STEPS = [
  {
    icon: BarChart2,
    title: "1. Collect utilization metrics",
    description:
      "Infralyst pulls CPU and memory utilization from CloudWatch for each EC2 instance, using up to 365 days of historical data.",
  },
  {
    icon: Search,
    title: "2. Run the analysis gates",
    description:
      "Each instance passes through data quality validation, utilization thresholds, and seasonality checks. An instance only gets a recommendation if all gates pass.",
  },
  {
    icon: GitPullRequest,
    title: "3. Generate the recommendation",
    description:
      "Infralyst recommends the next size down in the same instance family. When you're ready, an AI agent reads your Terraform, writes the change, and validates it. The analysis is fully deterministic.",
  },
] as const;

// ── Thresholds & Methodology ──────────────────────────────────────────────────

const GATES = [
  {
    gate: "Pre-flight",
    icon: ShieldCheck,
    checks: [
      { metric: "Ephemeral storage", threshold: "No instance store volumes" },
      { metric: "Minimum size", threshold: "Not already smallest in family" },
    ],
  },
  {
    gate: "Data quality",
    icon: BarChart2,
    checks: [
      { metric: "Data window", threshold: "≥ 30 days of metric data" },
      { metric: "Uptime coverage", threshold: "≥ 95% within the data window" },
    ],
  },
  {
    gate: "CPU & Memory",
    icon: Activity,
    checks: [
      { metric: "P99.5 · 1–60 days (5 min)", threshold: "< 40%" },
      { metric: "P95 · 60–365 days (hourly)", threshold: "< 40%" },
    ],
  },
  {
    gate: "Seasonality",
    icon: Calendar,
    description:
      "For instances with 60–365 days of data, P95 is checked per weekly bucket. If any week exceeds the 40% threshold, the instance is excluded.",
    checks: [],
  },
] as const;

// ── Trigger PRs from Slack ────────────────────────────────────────────────────

const SLACK_REC = RECOMMENDATIONS[1];

function TriggerFromSlack(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4">
          Trigger PRs{" "}
          <span className="text-primary">directly from Slack</span>
        </h2>
        <p className="text-center text-surface-foreground/80 mb-10 max-w-2xl mx-auto">
          Get notified when new savings are found. Review the details and
          generate a PR without leaving Slack.
        </p>

        <div className="max-w-xl mx-auto overflow-x-auto">
          <div className="min-w-[500px]">
            <SlackMessagePreview>
              {/* Header */}
              <p className="font-bold text-[15px] leading-snug mb-5">
                New EC2 savings recommendation · Save $23.00/mo
              </p>

              {/* All fields in one unified grid */}
              <div className="grid grid-cols-2 gap-x-10 mb-1">
                <div className="mb-4">
                  <p className="font-bold text-sm">Resource</p>
                  <p className="font-mono text-sm text-foreground/80">
                    {SLACK_REC.name} ({SLACK_REC.region})
                  </p>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">Lookback</p>
                  <p className="font-mono text-sm text-foreground/80">
                    {SLACK_REC.days}d
                  </p>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">CPU Avg</p>
                  <p className="font-mono text-sm text-foreground/80">
                    {SLACK_REC.cpuAvg}
                  </p>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">CPU P99.5</p>
                  <p className="font-mono text-sm text-foreground/80">
                    {SLACK_REC.cpuPct}
                  </p>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">Mem Avg</p>
                  <p className="font-mono text-sm text-foreground/80">
                    {SLACK_REC.memAvg}
                  </p>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">Mem P99.5</p>
                  <p className="font-mono text-sm text-foreground/80">
                    {SLACK_REC.memPct}
                  </p>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">Current</p>
                  <span className="font-mono text-sm text-surface-foreground/60">
                    {SLACK_REC.current}
                  </span>
                </div>
                <div className="mb-4">
                  <p className="font-bold text-sm">Recommended</p>
                  <span className="font-mono text-sm text-primary font-medium">
                    {SLACK_REC.recommended}
                  </span>
                </div>
              </div>

              {/* Link */}
              <p className="text-xs text-primary mb-3">View on Infralyst</p>

              {/* Buttons */}
              <div className="flex items-center gap-2">
                <button className="text-xs font-bold px-3 py-1.5 rounded-md bg-primary text-primary-foreground">
                  Generate PR
                </button>
                <button className="text-xs font-bold px-3 py-1.5 rounded-md border border-foreground/25 text-foreground/70">
                  Dismiss
                </button>
              </div>
            </SlackMessagePreview>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── What the PR Looks Like ────────────────────────────────────────────────────

const EC2_PR_BODY = (
  <>
    <p>
      EC2 instance <InlineCode>worker-batch-02</InlineCode> in{" "}
      <InlineCode>us-east-1</InlineCode> can be downsized from{" "}
      <InlineCode>c5.xlarge</InlineCode> to <InlineCode>c5.large</InlineCode>,
      saving an estimated $23/mo.
    </p>

    <div className="mb-4 pl-3 border-l-4 border-amber-400 text-sm text-amber-700 dark:text-amber-400">
      ⚠️ This change is not zero downtime. Plan for a maintenance window.
    </div>

    <table className="w-full text-sm border-collapse mb-4">
      <thead>
        <tr className="border-b border-foreground/20">
          <th className="text-left py-1 pr-4">Metric</th>
          <th className="text-left py-1 pr-4">Avg</th>
          <th className="text-left py-1">P99.5</th>
        </tr>
      </thead>
      <tbody>
        <tr className="border-b border-foreground/10">
          <td className="py-1 pr-4">CPU</td>
          <td className="py-1 pr-4">8.1%</td>
          <td className="py-1">22%</td>
        </tr>
        <tr>
          <td className="py-1 pr-4">Memory</td>
          <td className="py-1 pr-4">12%</td>
          <td className="py-1">28%</td>
        </tr>
      </tbody>
    </table>

    <p className="text-sm text-foreground/70">Lookback: 52 days</p>

    <p>
      <a
        href="https://infralyst.io/workspaces/acme-corp/aws_accounts/mock-uuid"
        className="text-primary underline"
      >
        View on Infralyst
      </a>
    </p>
  </>
);

const EC2_DIFF_LINES: DiffLine[] = [
  {
    type: "hunk",
    content:
      '@@ -42,8 +42,8 @@ resource "aws_iam_instance_profile" "cloudwatch_agent_profile" {',
  },
  { type: "context", content: "", oldLineNumber: 42, newLineNumber: 42 },
  {
    type: "context",
    content: 'resource "aws_instance" "worker_batch_02" {',
    oldLineNumber: 43,
    newLineNumber: 43,
  },
  {
    type: "context",
    content: "  ami           = data.aws_ami.amazon_linux.id",
    oldLineNumber: 44,
    newLineNumber: 44,
  },
  {
    type: "removed",
    content: '  instance_type = "c5.xlarge"',
    oldLineNumber: 45,
  },
  {
    type: "added",
    content: '  instance_type = "c5.large"',
    newLineNumber: 45,
  },
  {
    type: "context",
    content:
      "  iam_instance_profile = aws_iam_instance_profile.cloudwatch_agent_profile.name",
    oldLineNumber: 46,
    newLineNumber: 46,
  },
  {
    type: "context",
    content: "  tags = {",
    oldLineNumber: 47,
    newLineNumber: 47,
  },
  {
    type: "context",
    content: '    Name = "worker-batch-02"',
    oldLineNumber: 48,
    newLineNumber: 48,
  },
  {
    type: "context",
    content: "  }",
    oldLineNumber: 49,
    newLineNumber: 49,
  },
  { type: "context", content: "}", oldLineNumber: 50, newLineNumber: 50 },
];

function WhatThePrLooksLike(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4">
          What the <span className="text-primary">PR looks like</span>
        </h2>
        <p className="text-center text-surface-foreground/80 mb-10 max-w-2xl mx-auto">
          Infralyst opens a Terraform pull request in your repo. Your team
          reviews and merges it like any other change.
        </p>

        <PullRequestPreview
          title="Downsize EC2 instance worker-batch-02: c5.xlarge → c5.large (-$23/mo)"
          author="infralyst"
          status="open"
          body={EC2_PR_BODY}
          filesChangedContent={
            <DiffPreview filename="workers.tf" lines={EC2_DIFF_LINES} />
          }
        />
      </div>
    </section>
  );
}

// ── Safety & Guardrails ───────────────────────────────────────────────────────

const GUARDRAILS = [
  {
    icon: ShieldCheck,
    title: "Conservative thresholds",
    description:
      "Infralyst won't recommend rightsizing unless CPU and memory P99.5 (or P95 for longer windows) are comfortably below 40%. A single spike won't trigger a false recommendation.",
  },
  {
    icon: Calendar,
    title: "Seasonality-aware",
    description:
      "For instances with 60+ days of data, P95 utilization is checked per weekly bucket over the full observation window. If any week exceeds 40%, the instance is excluded. No recommendation, no risk.",
  },
  {
    icon: AlertTriangle,
    title: "Downtime warnings",
    description:
      "Changing an EC2 instance type requires a stop and start. Every PR includes a prominent warning so your team can plan a maintenance window.",
  },
  {
    icon: GitPullRequest,
    title: "PR-only changes",
    description:
      "Infralyst never applies changes directly. Every recommendation is delivered as a notification in Slack or in our console. You trigger PR generation, review, test, and merge on your timeline.",
  },
] as const;

// ── FAQ ───────────────────────────────────────────────────────────────────────

const EC2_FAQ_ITEMS: readonly FAQItem[] = [
  {
    question: "How do you identify oversized EC2 instances using CloudWatch metrics?",
    answer:
      "Pull CPU utilization and memory usage (if the CloudWatch agent is installed) over at least 30 days. Look at the P99.5. If it's well below the instance's capacity, the instance is a candidate for downsizing. Infralyst automates this analysis across your entire fleet, using a 40% P99.5 threshold on 5-minute metrics and additional seasonality checks on longer observation windows.",
  },
  {
    question: "What's the difference between rightsizing a standalone EC2 instance and an Auto Scaling Group?",
    answer:
      "Changing the instance type on a standalone EC2 instance requires a stop and start, so downtime is unavoidable. Infralyst flags this in every PR with a prominent warning so your team can plan a maintenance window. Auto Scaling Groups replace instances with the new type without a service interruption. Infralyst analyzes and generates PRs for both.",
  },
  {
    question: "What thresholds does Infralyst use when analyzing EC2 instances?",
    answer:
      "For instances with 1–60 days of data, Infralyst requires CPU and memory P99.5 to stay below 40% on 5-minute CloudWatch metrics. For 60–365 days, it checks P95 on hourly metrics and runs a seasonality check: P95 is evaluated per weekly bucket across the full observation window. If any single week exceeds 40%, the instance is excluded.",
  },
  {
    question: "How much CloudWatch data does Infralyst need before it will recommend an EC2 resize?",
    answer:
      "At least 30 days, with 95% or more uptime coverage within that window. Less data means too little signal to distinguish a genuine utilization pattern from a quiet period. Infralyst won't recommend a downsize based on a sample that doesn't reflect how the instance behaves.",
  },
  {
    question: "Which EC2 instances does Infralyst skip?",
    answer:
      "Three cases: instances already running the smallest type in their family, instances with instance store volumes attached (resizing would destroy local data), and instances that don't have enough CloudWatch history or uptime coverage to pass the data quality gate.",
  },
  {
    question: "How does Infralyst generate the Terraform change for an EC2 resize?",
    answer:
      "An AI agent reads your Terraform files, locates the instance_type argument for the target resource, writes the updated value, and validates the change before the PR is opened. The recommendation (which instance to resize and what type to use) is fully deterministic. The AI is only involved in the file editing step.",
  },
  {
    question: "What IAM permissions does Infralyst need for EC2 analysis?",
    answer:
      "A read-only IAM role. Infralyst reads CloudWatch metrics and EC2 instance metadata to run its analysis. It has no permissions to stop, start, or modify instances. Your team retains full control. Infralyst surfaces the opportunity; you decide whether to act.",
  },
  {
    question: "What happens if an EC2 resize causes a problem after the PR is merged?",
    answer:
      "Infralyst doesn't merge PRs. Your team does, after reviewing the utilization data in the PR body. If something goes wrong after a resize, reverting is a standard PR against the same Terraform file. The change is isolated to one instance_type argument, so the revert is fast.",
  },
  {
    question: "How often should you review EC2 instance sizing?",
    answer:
      "At least quarterly. Usage patterns shift as traffic grows, features change, and teams deploy new services. An instance sized correctly six months ago may be 40% oversized today. Infralyst runs this analysis continuously so you don't need to schedule manual reviews.",
  },
  {
    question: "Does EC2 rightsizing stay within the same instance family?",
    answer:
      "By default, yes. Infralyst recommends a smaller size within the same family (e.g., m5.xlarge to m5.large) to preserve CPU architecture, networking, and storage characteristics. Cross-family migrations like moving to Graviton are on the roadmap as a separate recommendation type.",
  },
] as const;

// ── Page ──────────────────────────────────────────────────────────────────────

export default function Ec2({
  articles,
}: {
  articles: Article[];
}): React.ReactElement {
  return (
    <Layout
      title="EC2 Rightsizing Tool"
      description="Analyze real CloudWatch utilization across your EC2 fleet. Infralyst generates ready-to-review Terraform PRs to rightsize instances and reduce costs."
    >
      <Hero />
      <TheProblem />
      <ProductHowItWorks steps={ANALYSIS_STEPS} />
      <ProductThresholds
        subtitle="Every gate and threshold Infralyst checks before recommending rightsizing. These are intentionally conservative. We'd rather miss a savings opportunity than recommend a change that causes problems."
        gates={GATES}
      />
      <WhatThePrLooksLike />
      <TriggerFromSlack />
      <ProductGuardrails
        guardrails={GUARDRAILS}
        crossLink={
          <>
            Also available for{" "}
            <Link
              href="/products/auto-scaling-groups"
              className="text-primary hover:underline"
            >
              Auto Scaling Groups
            </Link>
            ,{" "}
            <Link
              href="/products/ecs-fargate"
              className="text-primary hover:underline"
            >
              ECS Fargate
            </Link>
            , and{" "}
            <Link href="/products/ebs" className="text-primary hover:underline">
              EBS Volumes
            </Link>
            .
          </>
        }
      />
      <FAQ
        items={EC2_FAQ_ITEMS}
        title="EC2 Rightsizing FAQ"
        highlightWord="Rightsizing"
        className="py-16 px-4"
      />
      <LatestArticles
        articles={articles}
        title={
          <>
            Learn more about EC2{" "}
            <span className="text-primary">optimization</span>
          </>
        }
        subtitle={null}
      />
      <CallToAction
        heading="Start rightsizing your EC2 instances"
        subheading="Connect your AWS account and Infralyst will find instances worth rightsizing. When you're ready, generate the Terraform PR in one click."
      />
    </Layout>
  );
}
